// External Libraries
import React, { useState, useEffect } from "react";
import axios from "axios";
import { CSVLink } from "react-csv";

// Material-UI Components
import { Container, Card, CardContent, Chip, Stack  } from '@mui/material';
import { styled } from "@mui/system";
import Grid from '@mui/material/Grid2';

// Internal Components
import MainTable from "../table/MainTable";
import { RefreshCcw, RefreshCcwDot, RefreshCwOff } from "lucide-react";

/************* Amazing Charts Patients Table *************/

const AmazingChartsPatients = () => {
  const [amazinChartsPatientData, setAmazinChartsPatientData] = useState([]);
  const [pagination, setPagination] = useState({ pageIndex: 1, pageSize: 10, totalPages: 1 });

  const fetchPatients = async (pageIndex) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_APIS_BASE_URL}/api/powers-patients?page=${pageIndex}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        }
      );
      setAmazinChartsPatientData(response.data.data.data);
      setPagination((prev) => ({
        ...prev,
        pageIndex: response.data.data.current_page,
        totalPages: response.data.data.last_page,
      }));
    } catch (err) {
      console.error("Error fetching patients:", err);
    }
  };

  useEffect(() => {
    fetchPatients(pagination.pageIndex);
  }, [pagination.pageIndex]);

  const handlePageChange = (newPageIndex) => {
    if (typeof newPageIndex === 'object' && newPageIndex.hasOwnProperty('pageIndex')) {
      newPageIndex = newPageIndex.pageIndex;
    }

    if (typeof newPageIndex === 'number' && !isNaN(newPageIndex)) {
      setPagination((prev) => ({
        ...prev,
        pageIndex: newPageIndex + 1,
      }));
    } else {
      console.error("Invalid page index passed:", newPageIndex);
    }
  };

  const RotatingChip = styled(Chip)(({ theme }) => ({
    '&:hover .rotate-icon': {
      transform: 'rotate(180deg)',
      transition: 'transform 0.65s ease-in-out',
    },
  }));

  const columns = [
    {
      accessorKey: 'PatientID',
      header: 'PatientID',
    },
    {
      accessorKey: 'status',
      header: 'Status',
      Cell: ({ row }) => {
        const status = row.original.status;

        return (
          <>
            <Stack spacing={1}>
              <RotatingChip
                avatar={
                  status === 'Demo Mismatch' ? (
                    <RefreshCcwDot
                      className="rotate-icon"
                      size={16}
                      color="#ffffff"
                      strokeWidth={1.5}
                    />
                  ) : status === 'Not Found Tebra' ? (
                    <RefreshCwOff
                      className="rotate-icon"
                      size={16}
                      color="#ffffff"
                      strokeWidth={1.5}
                    />
                  ) : (
                    <RefreshCcw
                      className="rotate-icon"
                      size={16}
                      color="#ffffff"
                      strokeWidth={1.5}
                    />
                  )
                }
                label={status}
                variant="contained"
                color={status === 'Demo Mismatch' ? 'warning' : status === 'Not Found Tebra' ? 'error' : 'success'}
                sx={{borderRadius: '5px'}}
              />
            </Stack>
          </>
        );
      },
    },
    {
      accessorKey: 'FirstName',
      header: 'FirstName',
    },
    {
      accessorKey: 'MiddleName',
      header: 'MiddleName',
    },
    {
      accessorKey: 'LastName',
      header: 'LastName',
    },
    {
      accessorKey: 'PatientFullName',
      header: 'PatientFullName',
    },
    {
      accessorKey: 'DOB',
      header: 'DOB',
    },
    {
      accessorKey: 'Gender',
      header: 'Gender',
    },
    {
      accessorKey: 'EmailAddress',
      header: 'EmailAddress',
    },
    {
      accessorKey: 'HomePhone',
      header: 'HomePhone',
    },
    {
      accessorKey: 'WorkPhone',
      header: 'WorkPhone',
    },
    {
      accessorKey: 'AddressLine1',
      header: 'AddressLine1',
    },
    {
      accessorKey: 'City',
      header: 'City',
    },
    {
      accessorKey: 'State',
      header: 'State',
    },
    {
      accessorKey: 'ZipCode',
      header: 'ZipCode',
    },
    {
      accessorKey: 'MedicalRecordNumber',
      header: 'MedicalRecordNumber',
    },
    {
      accessorKey: 'PrimaryInsurancePolicyCompanyName',
      header: 'PrimaryInsurancePolicyCompanyName',
    },
    {
      accessorKey: 'PrimaryInsurancePolicyNumber',
      header: 'PrimaryInsurancePolicyNumber',
    },
    {
      accessorKey: 'SecondaryInsurancePolicyCompanyName',
      header: 'SecondaryInsurancePolicyCompanyName',
    },
    {
      accessorKey: 'SecondaryInsurancePolicyNumber',
      header: 'SecondaryInsurancePolicyNumber',
    },
    {
      accessorKey: 'DemoPatientComments',
      header: 'DemoPatientComments',
    },
  ];

  const csvData = amazinChartsPatientData.map(patient => {
    const row = {};
    columns.forEach(column => {
      row[column.header] = patient[column.accessorKey] || '';
    });
    return row;
  });

  const renderTopToolbarCustomActions = () => (
    <div className="d-flex gap-2">
      <CSVLink
        data={csvData}
        filename={"patients_data.csv"}
        className="btn btn-primary light btn-sm"
      >
        <i className="fa-solid fa-file-excel me-2" />
        Export Report
      </CSVLink>
    </div>
  );

  return (
    <>
      <Container maxWidth="xxl" sx={{ padding: '1.875rem 1.875rem 0' }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Card elevation={0} sx={{ borderRadius: '12px' }}>
              <CardContent>
                <MainTable
                  data={amazinChartsPatientData}
                  columns={columns}
                  title="Amazing Charts Patients"

                  enableRowSelection={false}
                  enableColumnFilters={true}
                  enableGlobalFilter={true}
                  enablePagination={true}
                  enableSorting={true}
                  enableGrouping={true}
                  manualPaginationMode={true}
                  paginationDisplayMode="pages"
                  currentPage={pagination.pageIndex - 1}
                  totalPages={pagination.totalPages}
                  onPageChange={handlePageChange}

                  enableStickyHeader={true}
                  enableStickyFooter={true}
                  enableColumnResizing={true}
                  enableHiding={true}

                  rowsPerPageOptions={[10, 25, 50, 100]}
                  initialPageSize={10}

                  renderTopToolbarCustomActions={renderTopToolbarCustomActions}

                  muiTablePaperProps={{
                    elevation: 0,
                    sx: {
                      boxShadow: 'none',
                      border: '1px solid rgba(0, 0, 0, 0.12)',
                    }
                  }}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default AmazingChartsPatients;
